import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeOnboarding, OnboardingAnswers, Persona, OutreachChannel } from '../../api/onboarding';
import SingleSelectGrid, { OptionTile } from './SingleSelectGrid';
import BusinessTypeChipPicker from './BusinessTypeChipPicker';
import LocationStepContent from './LocationStepContent';
import SuccessDefinitionStep from './SuccessDefinitionStep';

type Stage = 'welcome' | 'question' | 'done';

interface BaseStep {
  title: string;
  subtitle?: string;
}

interface SingleStep extends BaseStep {
  kind: 'single';
  key: 'persona' | 'outreach_channel';
  options: OptionTile[];
}

interface ChipsStep extends BaseStep {
  kind: 'chips';
}

interface LocationStep extends BaseStep {
  kind: 'location';
}

interface TextStep extends BaseStep {
  kind: 'text';
}

type Step = SingleStep | ChipsStep | LocationStep | TextStep;

const STEPS: Step[] = [
  {
    key: 'persona',
    kind: 'single',
    title: 'Which best describes you?',
    options: [
      { id: 'agency', label: 'I run an agency', hint: 'SEO, ads, web design, marketing' },
      { id: 'freelancer', label: "I'm a freelancer or consultant", hint: 'Working solo' },
      { id: 'sales', label: 'I do sales or lead gen for a company', hint: 'Finding prospects for a team' },
      { id: 'own_business', label: 'I run a local business', hint: 'Looking for my own customers' },
    ],
  },
  {
    kind: 'chips',
    title: 'What kind of businesses are your customers?',
    subtitle: 'Pick up to 3, or type your own.',
  },
  {
    kind: 'location',
    title: 'Where are your customers?',
  },
  {
    key: 'outreach_channel',
    kind: 'single',
    title: 'How will you reach them?',
    options: [
      { id: 'email', label: 'Cold email' },
      { id: 'phone', label: 'Phone calls' },
      { id: 'linkedin', label: 'LinkedIn' },
      { id: 'ads', label: 'Ads audience / upload lists' },
      { id: 'undecided', label: 'Not decided yet' },
    ],
  },
  {
    kind: 'text',
    title: 'What would make these 7 days a success for you?',
  },
];

const TOTAL = STEPS.length;

const EMPTY_ANSWERS: OnboardingAnswers = {
  persona: null,
  target_business_types: [],
  target_country: null,
  target_region: null,
  outreach_channel: null,
  success_definition: null,
};

function isStepComplete(step: Step, answers: OnboardingAnswers): boolean {
  switch (step.kind) {
    case 'single':
      return answers[step.key] !== null;
    case 'chips':
      return answers.target_business_types.length > 0;
    case 'location':
    case 'text':
      return true;
  }
}

const SEARCH_QUERY_STRICT_PATTERN = /^\p{L}[\p{L}\p{M}]*(?:[ '\-’][\p{L}\p{M}]+)*$/u;

function sanitizeForSearchQueryPrefill(raw: string): string | null {
  const withAnd = raw.replace(/&/g, 'and');
  const stripped = withAnd.replace(/[^\p{L}\p{M} '\-’]/gu, ' ');
  const collapsed = stripped.replace(/\s+/g, ' ').trim().normalize('NFC');
  return SEARCH_QUERY_STRICT_PATTERN.test(collapsed) ? collapsed : null;
}

interface Props {
  onComplete: () => void;
  username?: string;
}

export const OnboardingModal: React.FC<Props> = ({ onComplete, username }) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('welcome');
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState<OnboardingAnswers>(EMPTY_ANSWERS);
  const [submitting, setSubmitting] = useState(false);

  const step = STEPS[stepIdx];

  const progressPct =
    stage === 'welcome' ? 0
    : stage === 'done' ? 100
    : Math.round(((stepIdx + 1) / TOTAL) * 100);

  const submitAnswers = async (finalAnswers: OnboardingAnswers) => {
    setSubmitting(true);
    try {
      await completeOnboarding(finalAnswers);
    } catch {
      // best-effort — don't block the user
    } finally {
      setSubmitting(false);
      setStage('done');
    }
  };

  const handleNext = async () => {
    if (stage === 'welcome') {
      setStage('question');
      setStepIdx(0);
      return;
    }
    if (stepIdx < TOTAL - 1) {
      setStepIdx(s => s + 1);
    } else {
      const trimmedSuccess = answers.success_definition?.trim() ?? '';
      await submitAnswers({ ...answers, success_definition: trimmedSuccess || null });
    }
  };

  const handleSkipFinal = async () => {
    await submitAnswers({ ...answers, success_definition: null });
  };

  const handleBack = () => {
    if (stage === 'question') {
      if (stepIdx === 0) {
        setStage('welcome');
      } else {
        setStepIdx(s => s - 1);
      }
    }
  };

  const handleContinue = () => {
    const firstType = answers.target_business_types[0];
    const query = firstType ? sanitizeForSearchQueryPrefill(firstType) : null;
    if (query) {
      navigate('/searches/new', { state: { search_query: query, from: 'onboarding' } });
    } else {
      navigate('/searches/new');
    }
    onComplete();
  };

  const handleChangeSomething = () => {
    navigate('/searches/new');
    onComplete();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/40 backdrop-blur-md">
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="px-8 pt-6 pb-0 shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary text-white grid place-items-center font-bold text-sm">W</div>
            <span className="text-sm font-semibold text-secondary">WebLeads</span>
            {stage === 'question' && (
              <span className="ml-2 text-xs text-gray-400">
                Step {stepIdx + 1} of {TOTAL}
              </span>
            )}
          </div>
          <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {stage === 'welcome' && <WelcomeScreen onStart={handleNext} username={username} />}
          {stage === 'question' && (
            <StepContent step={step} answers={answers} setAnswers={setAnswers} />
          )}
          {stage === 'done' && (
            <DoneScreen
              answers={answers}
              onContinue={handleContinue}
              onChangeSomething={handleChangeSomething}
            />
          )}
        </div>

        {/* Footer — questions only */}
        {stage === 'question' && (
          <div className="flex items-center gap-3 px-8 py-4 border-t border-gray-100 bg-gray-50/60 shrink-0">
            <button
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-secondary px-3 py-2 rounded-lg hover:bg-white transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M15 19l-7-7 7-7" /></svg>
              Back
            </button>

            <div className="hidden sm:flex items-center gap-1.5 ml-2">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className={[
                    'h-1.5 rounded-full transition-all',
                    i < stepIdx ? 'w-6 bg-primary' : i === stepIdx ? 'w-10 bg-primary' : 'w-6 bg-gray-200',
                  ].join(' ')}
                />
              ))}
            </div>

            <div className="ml-auto flex items-center gap-4">
              {step.kind === 'text' && (
                <button
                  type="button"
                  onClick={handleSkipFinal}
                  disabled={submitting}
                  className="text-sm font-medium text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  Skip
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!isStepComplete(step, answers) || submitting}
                className={[
                  'flex items-center gap-2 font-semibold text-sm px-5 py-2.5 rounded-xl transition-all',
                  isStepComplete(step, answers) && !submitting
                    ? 'bg-primary hover:bg-primary-hover text-white shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                ].join(' ')}
              >
                {submitting ? 'Saving…' : stepIdx === TOTAL - 1 ? 'Finish' : 'Next'}
                {!submitting && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 5l7 7-7 7" /></svg>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function WelcomeScreen({ onStart, username }: { onStart: () => void; username?: string }) {
  const displayName = username
    ? username.charAt(0).toUpperCase() + username.slice(1)
    : 'there';

  return (
    <div className="py-4">
      <div className="flex items-center gap-2 mb-6 text-xs font-semibold tracking-widest uppercase text-gray-400">
        <svg className="w-3.5 h-3.5 animate-spin text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
        Let's build your first list · 1 minute
      </div>
      <h2 className="text-3xl font-bold text-secondary leading-snug mb-2">
        Welcome, {displayName}.
      </h2>
      <h2 className="text-3xl font-bold leading-snug mb-5">
        <span className="text-secondary">Let's set WebLeads up around </span>
        <span className="text-primary">how you actually work.</span>
      </h2>
      <p className="text-gray-500 text-[15px] leading-relaxed mb-8 max-w-md">
        A few quick questions so your first search is set up the way you work.
      </p>
      <button
        onClick={onStart}
        className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold text-sm px-6 py-3 rounded-xl shadow-md transition-all"
      >
        Let's go
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 5l7 7-7 7" /></svg>
      </button>
    </div>
  );
}

function StepContent({
  step,
  answers,
  setAnswers,
}: {
  step: Step;
  answers: OnboardingAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<OnboardingAnswers>>;
}) {
  switch (step.kind) {
    case 'single':
      return (
        <SingleSelectGrid
          title={step.title}
          options={step.options}
          selected={answers[step.key]}
          onSelect={id => {
            if (step.key === 'persona') {
              setAnswers(a => ({ ...a, persona: id as Persona }));
            } else {
              setAnswers(a => ({ ...a, outreach_channel: id as OutreachChannel }));
            }
          }}
        />
      );
    case 'chips':
      return (
        <div>
          <h2 className="text-2xl font-bold text-secondary leading-snug mb-1">{step.title}</h2>
          {step.subtitle && <p className="text-sm text-gray-500 mb-6">{step.subtitle}</p>}
          <BusinessTypeChipPicker
            value={answers.target_business_types}
            onChange={next => setAnswers(a => ({ ...a, target_business_types: next }))}
          />
        </div>
      );
    case 'location':
      return (
        <div>
          <h2 className="text-2xl font-bold text-secondary leading-snug mb-6">{step.title}</h2>
          <LocationStepContent
            country={answers.target_country}
            region={answers.target_region}
            onChange={(country, region) => setAnswers(a => ({ ...a, target_country: country, target_region: region }))}
          />
        </div>
      );
    case 'text':
      return (
        <div>
          <h2 className="text-2xl font-bold text-secondary leading-snug mb-6">{step.title}</h2>
          <SuccessDefinitionStep
            value={answers.success_definition}
            onChange={value => setAnswers(a => ({ ...a, success_definition: value }))}
          />
        </div>
      );
  }
}

function DoneScreen({
  answers,
  onContinue,
  onChangeSomething,
}: {
  answers: OnboardingAnswers;
  onContinue: () => void;
  onChangeSomething: () => void;
}) {
  const firstBusinessType = answers.target_business_types[0];

  if (!firstBusinessType) {
    return (
      <div className="py-4 text-center">
        <div className="w-14 h-14 rounded-2xl bg-primary text-white grid place-items-center mx-auto mb-5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
            <path d="M5 12.5l4.5 4.5L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-secondary mb-2">You're all set.</h2>
        <p className="text-gray-500 text-sm mb-8 max-w-sm mx-auto">
          Your workspace is configured. Start your first search whenever you're ready.
        </p>
        <button
          onClick={onChangeSomething}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold text-sm px-6 py-3 rounded-xl shadow-md transition-all"
        >
          Go to dashboard
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
    );
  }

  return (
    <div className="py-4 text-center">
      <div className="w-14 h-14 rounded-2xl bg-primary text-white grid place-items-center mx-auto mb-5">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
          <path d="M5 12.5l4.5 4.5L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-secondary mb-2">Ready to find {firstBusinessType}.</h2>
      <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
        Add the location on the next screen and we'll start.
      </p>

      <div className="max-w-sm mx-auto mb-8 rounded-xl border border-gray-200 bg-gray-50 p-4 text-left">
        <div className="flex gap-2 text-sm">
          <span className="font-medium text-gray-500 shrink-0">Business type:</span>
          <span className="text-secondary font-semibold">{firstBusinessType}</span>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4">
        <button
          onClick={onContinue}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-semibold text-sm px-6 py-3 rounded-xl shadow-md transition-all"
        >
          Continue
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 5l7 7-7 7" /></svg>
        </button>
        <button
          onClick={onChangeSomething}
          className="text-sm font-medium text-gray-500 hover:text-secondary"
        >
          Change something
        </button>
      </div>
    </div>
  );
}

export default OnboardingModal;
